import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { generateQuotationPDF } from './QuotationPDF';

export type QuoteItem = {
  route: string;
  unit: string;
  detail: string;
  qty: number;
  unitPrice: number;
};

export type QuoteData = {
  quotationNumber: string;
  date: string; // ISO or locale string
  clientName: string;
  clientRuc?: string;
  clientContact?: string;
  clientEmail?: string;
  clientPhone?: string;
  validityDays?: number; // e.g., 15
  paymentTerms?: string; // e.g., "50% Adelantado y 50% ..."
  availability?: string; // e.g., "De acuerdo a confirmación"
  loadType?: string; // e.g., "Carga y Descarga"
  currency?: 'PEN' | 'USD';
  items: QuoteItem[];
  includeIgv?: boolean; // If false, show legend "No incluyen IGV"
  additionalNotes?: string[]; // bullets
};

const defaultNotes: string[] = [

  'Alcance del servicio: El servicio comprende el transporte de la carga desde ORIGEN hasta DESTINO. No incluye servicios de estiba ni cuadrilla.',
  'Tiempo de tránsito: El tiempo estimado de tránsito es de * días. Este plazo no contempla posibles demoras por congestión vehicular u otras contingencias viales.',
  'Valor del flete: El valor del flete internacional es neto, exento de IGV.',
  'Reserva de unidad: La separación de la unidad deberá realizarse con una anticipación mínima de 4 días.',
  'Almacenes fronterizos: Se otorgan 12 horas libres de almacenamiento tanto en el punto de origen como en el de destino.',
  'Gastos adicionales:',
  'Stand by: USD 420 por día.',
  'Falso flete: 100% del valor del flete acordado.',
  'Condiciones de pago: El pago del flete se realizará de la siguiente manera:',
  '70% de adelanto previo a la salida de la unidad.',
  '30% restante antes de la descarga de la mercancía.',
  'Exclusiones: Este servicio no incluye agenciamiento de aduanas ni contratación de almacenes adicionales.',
];

const QuoteForm: React.FC = () => {
  const [data, setData] = useState<QuoteData>({
    quotationNumber: '',
    date: new Date().toISOString().slice(0,10),
    clientName: '',
    clientRuc: '',
    clientContact: '',
    clientEmail: '',
    clientPhone: '',
    validityDays: 15,
    paymentTerms: '70% adelantado y 30% antes de la descarga',
    availability: 'De acuerdo a confirmación',
    loadType: 'Carga y descarga',
    currency: 'PEN',
    includeIgv: false,
    items: [
      { route: '', unit: '', detail: '', qty: 1, unitPrice: 0 },
    ],
    additionalNotes: defaultNotes,
  });

  const total = useMemo(() => data.items.reduce((acc, it) => acc + (it.qty || 0) * (it.unitPrice || 0), 0), [data.items]);

  // Generar número de cotización auto (COT-YYYYMMDD-###)
  const generateQuoteNumber = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const key = `royal_quote_seq_${y}${m}${d}`;
    const seq = Number(localStorage.getItem(key) || '0') + 1;
    localStorage.setItem(key, String(seq));
    return `COT-${y}${m}${d}-${String(seq).padStart(3, '0')}`;
  };

  useEffect(() => {
    if (!data.quotationNumber) {
      setData(s => ({ ...s, quotationNumber: generateQuoteNumber() }));
    }
  }, []);

  const updateItem = (idx: number, patch: Partial<QuoteItem>) => {
    setData(s => {
      const items = [...s.items];
      items[idx] = { ...items[idx], ...patch } as QuoteItem;
      return { ...s, items };
    });
  };

  const addItem = () => setData(s => ({ ...s, items: [...s.items, { route: '', unit: '', detail: '', qty: 1, unitPrice: 0 }] }));
  const removeItem = (idx: number) => setData(s => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const onGenerate = async () => {
    await generateQuotationPDF(data);
  };

  const currencySymbol = data.currency === 'USD' ? '$' : 'S/';

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">N° Cotización</label>
          <div className="mt-1 flex gap-2">
            <input value={data.quotationNumber} onChange={e => setData({ ...data, quotationNumber: e.target.value })} className="flex-1 border rounded-md px-3 py-2" placeholder="COT-20250930-001" />
            <button type="button" onClick={() => setData(s => ({ ...s, quotationNumber: generateQuoteNumber() }))} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Auto</button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Fecha</label>
          <input type="date" value={data.date} onChange={e => setData({ ...data, date: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Moneda</label>
          <select value={data.currency} onChange={e => setData({ ...data, currency: e.target.value as any })} className="mt-1 w-full border rounded-md px-3 py-2">
            <option value="PEN">Soles (PEN)</option>
            <option value="USD">Dólares (USD)</option>
          </select>
        </div>
      </div>

      {/* Client */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Cliente</label>
          <input value={data.clientName} onChange={e => setData({ ...data, clientName: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="ROYAL TRUCKING E.I.R.L." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">RUC</label>
          <input value={data.clientRuc} onChange={e => setData({ ...data, clientRuc: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="20611798572" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Contacto</label>
          <input value={data.clientContact} onChange={e => setData({ ...data, clientContact: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="Juan Pérez" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input value={data.clientEmail} onChange={e => setData({ ...data, clientEmail: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="ventas@royal.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Teléfono</label>
            <input value={data.clientPhone} onChange={e => setData({ ...data, clientPhone: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="+51 999 999 999" />
          </div>
        </div>
      </div>

      {/* Conditions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Validez de la oferta (días)</label>
          <input type="number" value={data.validityDays ?? 0} onChange={e => setData({ ...data, validityDays: Number(e.target.value) })} className="mt-1 w-full border rounded-md px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Condiciones de pago</label>
          <input value={data.paymentTerms || ''} onChange={e => setData({ ...data, paymentTerms: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Disponibilidad</label>
          <input value={data.availability || ''} onChange={e => setData({ ...data, availability: e.target.value })} className="mt-1 w-full border rounded-md px-3 py-2" />
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Detalle de la cotización</h3>
          <button type="button" onClick={addItem} className="inline-flex items-center px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"><Plus className="h-4 w-4 mr-1"/>Agregar ítem</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="p-2 text-left">Ruta</th>
                <th className="p-2 text-left">Unidad</th>
                <th className="p-2 text-left">Detalle</th>
                <th className="p-2 text-right">Cant.</th>
                <th className="p-2 text-right">P. Unitario</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2"/>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2"><input className="w-full border rounded px-2 py-1" value={it.route} onChange={e => updateItem(idx, { route: e.target.value })} placeholder="PUERTO BERMUDEZ → VILLA MARIA"/></td>
                  <td className="p-2"><input className="w-full border rounded px-2 py-1" value={it.unit} onChange={e => updateItem(idx, { unit: e.target.value })} placeholder="PLATAFORMA / SEMI"/></td>
                  <td className="p-2"><input className="w-full border rounded px-2 py-1" value={it.detail} onChange={e => updateItem(idx, { detail: e.target.value })} placeholder="MADERA / TORNILLO 26 TON."/></td>
                  <td className="p-2 text-right"><input type="number" className="w-24 border rounded px-2 py-1 text-right" value={it.qty} onChange={e => updateItem(idx, { qty: Number(e.target.value) })}/></td>
                  <td className="p-2 text-right"><input type="number" className="w-32 border rounded px-2 py-1 text-right" value={it.unitPrice} onChange={e => updateItem(idx, { unitPrice: Number(e.target.value) })}/></td>
                  <td className="p-2 text-right">{currencySymbol} {(it.qty * it.unitPrice).toFixed(2)}</td>
                  <td className="p-2 text-center"><button onClick={() => removeItem(idx)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4"/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Notas (una por línea)</label>
        <textarea className="mt-1 w-full border rounded-md px-3 py-2" rows={4} value={(data.additionalNotes || []).join('\n')} onChange={(e) => setData({ ...data, additionalNotes: e.target.value.split('\n').filter(Boolean) })}/>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
            <input type="checkbox" className="rounded" checked={!!data.includeIgv} onChange={(e) => setData({ ...data, includeIgv: e.target.checked })} />
            <span>Precios incluyen IGV</span>
          </label>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Importe</div>
          <div className="text-2xl font-bold">{currencySymbol} {total.toFixed(2)}</div>
        </div>
      </div>

      <div className="pt-2 flex justify-end">
        <button type="button" onClick={onGenerate} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Generar PDF</button>
      </div>
    </div>
  );
};

export default QuoteForm;
